<template>
    <section class="school-edit-view">
        <app-form
            ref="schoolForm"
            :model="value"
            :rules="rules"
            @submit.native.prevent="submit">
            <p class="required-fields">All fields marked with * are required</p>
            <grid>
                <grid-item width="7" mobile-width="12">
                    <app-form-item label="Name" prop="name">
                        <app-input id="name" v-model="value.name"/>
                    </app-form-item>
                </grid-item>
                <grid-item width="12">
                    <app-form-item label="Address" prop="address">
                        <app-input
                            id="address"
                            v-model="value.address"
                            :rows="3"
                            type="textarea"/>
                    </app-form-item>
                </grid-item>
                <grid-item width="6">
                    <app-form-item label="Contact Person" prop="contact_person">
                        <app-input id="contact_person" v-model="value.contact_person"/>
                    </app-form-item>
                </grid-item>
                <grid-item width="6">
                    <app-form-item label="Phone" prop="phone">
                        <app-input id="phone" v-model="value.phone"/>
                    </app-form-item>
                </grid-item>
                <grid-item width="4" mobile-width="6">
                    <app-form-item label="Late Time" prop="late_time">
                        <app-input id="late_time" v-model.number="value.late_time">
                            <template slot="append">minutes</template>
                        </app-input>
                    </app-form-item>
                </grid-item>
                <grid-item width="4" mobile-width="6">
                    <app-form-item label="Absent Time" prop="absent_time">
                        <app-input id="absent_time" v-model.number="value.absent_time">
                            <template slot="append">minutes</template>
                        </app-input>
                    </app-form-item>
                </grid-item>
                <grid-item width="4" mobile-width="6">
                    <app-form-item label="Leave Early Time" prop="leave_time">
                        <app-input id="leave_time" v-model.number="value.leave_time">
                            <template slot="append">minutes</template>
                        </app-input>
                    </app-form-item>
                </grid-item>
                <grid-item width="5" mobile-width="6">
                    <app-form-item label="Warning Percentage" prop="warning_percentage">
                        <app-input id="warning_percentage" v-model.number="value.warning_percentage">
                            <template slot="append">%</template>
                        </app-input>
                    </app-form-item>
                </grid-item>
                <grid-item width="7">
                    <app-form-item label="Time Zone" prop="timezone">
                        <app-select
                            id="timezone"
                            v-model="value.timezone"
                            placeholder="Select">
                            <app-option
                                v-for="item in timezones"
                                :key="item.value"
                                :label="item.label"
                                :value="item.value"/>
                        </app-select>
                    </app-form-item>
                </grid-item>
            </grid>
            <app-collapse-item title="Advanced Settings" name="1">
                <grid>
                    <grid-item width="6">
                        <app-form-item label="Token Pattern" prop="token_pattern">
                            <div slot="label">
                                Token Pattern
                                <app-tooltip placement="top">
                                    <div slot="content">
                                        The value to which a students<br>
                                        token will be set both on<br>
                                        creation and on reset.
                                    </div>
                                    <i class="icon-info"/>
                                </app-tooltip>
                            </div>
                            <app-select
                                id="token_pattern"
                                v-model="value.token_pattern"
                                placeholder="Select">
                                <app-option
                                    v-for="item in patterns"
                                    :key="item.value"
                                    :label="item.label"
                                    :value="item.value"/>
                            </app-select>
                        </app-form-item>
                    </grid-item>
                    <grid-item width="6">
                        <app-form-item
                            label="Allow Users To Invite Users"
                            prop="allow_user_invite"
                            class="invite-toggle">
                            <app-switch
                                v-model="value.allow_user_invite"
                                active-text="Yes"
                                inactive-text="No"/>
                        </app-form-item>
                    </grid-item>
                </grid>
            </app-collapse-item>
            <app-form-item>
                <app-button
                    :loading="isSaving"
                    type="primary"
                    native-type="submit"
                    size="large">Save</app-button>
            </app-form-item>
        </app-form>
    </section>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import School from '@/store/models/School'

export default {
    name: 'school-edit',
    components: {},
    props: ['value'],
    inject: {
        modalOptions: {
            default() {
                return {}
            }
        }
    },
    data() {
        return {
            isSaving: false,
            advancedSettings: [1],
            timezones: School.timezones,
            patterns: [
                { label: 'Random', value: 'random' },
                { label: 'Email Address', value: 'email' },
                { label: 'Identifier', value: 'identifier' }
            ],
            rules: {
                name: [
                    {
                        required: true,
                        message: 'Please enter a name',
                        trigger: 'blur'
                    }
                ],
                late_time: [
                    {
                        required: true,
                        type: 'number',
                        message: 'Please enter a late time',
                        trigger: 'blur'
                    }
                ],
                absent_time: [
                    {
                        required: true,
                        type: 'number',
                        message: 'Please enter an absent time',
                        trigger: 'blur'
                    }
                ],
                leave_time: [
                    {
                        required: true,
                        type: 'number',
                        message: 'Please enter a leave time',
                        trigger: 'blur'
                    }
                ],
                warning_percentage: [
                    {
                        required: true,
                        type: 'number',
                        message: 'Please fill',
                        trigger: 'blur'
                    }
                ],
                timezone: [
                    {
                        required: true,
                        message: 'Please select',
                        trigger: 'blur'
                    }
                ]
            }
        }
    },
    computed: {
        ...mapGetters(['role'])
    },
    methods: {
        ...mapActions(['switchSchool']),
        submit() {
            this.$refs['schoolForm'].validate(valid => {
                if (valid) {
                    this.isSaving = true
                    this.value.id ? this.update() : this.create()
                }
            })
        },
        create() {
            const school = new School(this.value)
            school.$create().then(({ data }) => {
                this.isSaving = false
                this.modalOptions.modalOpen = false
                this.switchSchool({ schoolId: data.id }).then(() => {
                    this.$router.push({
                        name: 'settings.school',
                        params: { id: school.id }
                    })
                    location.reload()
                })
            })
            return false
        },
        update() {
            this.value.$update().then(() => {
                this.isSaving = false
                this.modalOptions.modalOpen = false
            })
            return false
        }
    }
}
</script>

<style lang="stylus"></style>
